import React, { useState } from 'react';
import { Box, Paper, useMediaQuery, useTheme, Button, Fab, Menu as MuiMenu, MenuItem } from '@mui/material';
import { Upload, Menu, Plus } from 'lucide-react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../components/topbar';
import CategoryTable, { Category } from '../components/category-table';
import AddCategoryPopup from '../components/popup/add-category';
import DateRangeFilter from '../components/ui/date-range-filter';
import AddBtn from '../components/ui/add-btn';
import DeletePopup from '../components/popup/delete';
import DeletePopupMobile from '../components/popup/mobile/delete-mobile';
import ReassignCategoryModal from '../components/popup/reassign-category';
import { TreeDropdownOption } from '../components/tree-dropdown';
import categoryImage from '../files/images/category.png';
import refuseImage from '../files/images/refuse.png';
import SidebarMobileDrawer from '../components/sidebar-mobile-drawer';
import { useSidebarMobile } from '../providers/sidebar-mobile-context';
import ImportCategoryPopup from '../components/popup/import-category';
import { FileDetails } from '../pages/transactions';
import XeroSyncButton from '../components/xero-sync-button';

import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import { useUser } from '../providers/user-provider';
import { useToast } from '../providers/toaster-provider';
import { usePermissions } from '../providers/permissions-provider';

const CategoriesPage: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const { user } = useUser();
    const { can } = usePermissions();
    const canCreate = can('categories', 'create');
    const canUpdate = can('categories', 'update');
    const canDelete = can('categories', 'delete');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    const [openModal, setOpenModal] = React.useState<string>('');
    const [parent, setParent] = React.useState<{ code: string, id: number } | null>(null);
    const [categories, setCategories] = React.useState<Category[]>([]);
    const [allCodes, setAllCodes] = React.useState<string[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [dateRange, setDateRange] = useState<{ start: Date | null, end: Date }>({ start: null, end: new Date() });
    const [filter, setFilter] = useState<{ codes: string[]; name: string }>({ codes: [], name: '' });
    const [sort, setSort] = useState<{ field: string, direction: 'asc' | 'desc' }>({ field: '', direction: 'desc' });
    const [fabMenuAnchor, setFabMenuAnchor] = React.useState<null | HTMLElement>(null);

    // import-related state
    const [importFileDetails, setImportFileDetails] = React.useState<FileDetails | null>(null);
    const [importId, setImportId] = React.useState<string | null>(null);

    const [openNoProviderPopup, setOpenNoProviderPopup] = React.useState(false);

    const [createImport, setCreateImport] = useApi();

    const [fetch, setFetch] = useApi();
    const [create, setCreate] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();
    const [reassign, setReassign] = useApi();

    const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);

    React.useEffect(() => {
        document.title = "FineAnts | Categories";
    }, []);

    // handle provider fetch when user attempts import
    const handleImportClick = () => {
        setOpenModal('import');
    };

    React.useEffect(() => {
        const params = [
            { param: 'entityId', value: user?.entityId },
            { param: 'startDate', value: dateRange.start?.toISOString().split('T')[0] },
            { param: 'endDate', value: dateRange.end.toISOString().split('T')[0] },
            ...(filter.codes.length > 0 ? [{ param: 'filterCodes', value: filter.codes.join(',') }] : []),
            ...(filter.name ? [{ param: 'filterName', value: filter.name }] : []),
            ...(sort.field ? [{ param: 'sortBy', value: sort.field }, { param: 'sortOrder', value: sort.direction }] : []),
        ];
        const { url, method } = buildApiRequestConfig(routes.categories.fetchByEntity, params);
        setFetch({ url, method });
    }, [create, update, remove, user, dateRange, filter, sort]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (!isLoading && data) {
            const transformCategory = (cat: any): Category => ({
                id: cat.id,
                code: cat.code,
                name: cat.name,
                searchStrings: cat.searchString,
                balance: cat.balance,
                transactionCount: cat.transactionCount,
                children: cat.children ? cat.children.map(transformCategory) : undefined,
            });

            const categoriesData = data.map(transformCategory);
            
            // Find the earliest date from all categories and subcategories if it exists
            const findEarliestDate = (categories: any[]): Date | null => {
                let earliestDate: Date | null = null;
                
                const processCategory = (cat: any) => {
                    if (cat.earliestDate) {
                        const catDate = new Date(cat.earliestDate);
                        if (!earliestDate || catDate < earliestDate) {
                            earliestDate = catDate;
                        }
                    }
                    
                    if (cat.children && cat.children.length > 0) {
                        cat.children.forEach(processCategory);
                    }
                };
                
                categories.forEach(processCategory);
                return earliestDate;
            };
            
            const earliestDate = findEarliestDate(data);
            
            // Set earliest date if it exists and we haven't set a start date yet
            if (earliestDate && !dateRange.start) {
                setDateRange(prev => ({ ...prev, start: earliestDate }));
            }
            
            // Accumulate all known codes so the filter list never shrinks when a filter is active
            const flattenCodes = (cats: Category[]): string[] =>
                cats.flatMap(c => [c.code, ...(c.children ? flattenCodes(c.children) : [])]);
            const incoming = flattenCodes(categoriesData);
            setAllCodes(prev => Array.from(new Set([...prev, ...incoming])).sort());

            // Always show the data immediately
            setCategories(categoriesData);
            setInitialLoadComplete(true);
        }
    }, [fetch, dateRange.start]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (data && !isLoading) {
            showSuccess(`${data.isChildren ? 'Sub-category' : 'Category'} created successfully`);
            setOpenModal('');
        }
        if (error) {
            showError(error ? error.toString() : "Failed to create category");
        }
    }, [create]);

    React.useEffect(() => {
        const { data, isLoading, error } = update;
        if (data && !isLoading) {
            showSuccess("Category updated successfully");
            setOpenModal('');
            setSelectedCategoryId(null);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to update category");
        }
    }, [update]);

    React.useEffect(() => {
        const { data, isLoading, error } = remove;
        if (data && !isLoading) {
            showSuccess("Category deleted successfully");
            setSelectedCategoryId(null);
            setOpenModal('');
        }
        if (error) {
            showError(error ? error.toString() : "Failed to delete category");
        }
    }, [remove]);

    React.useEffect(() => {
        const { data, isLoading, error } = reassign;
        if (data && !isLoading) {
            showSuccess("Transactions reassigned successfully");
            // Auto-delete the category after successful reassignment
            if (!selectedCategoryId) return;
            const { url, method } = buildApiRequestConfig(routes.categories.delete, [{ param: 'id', value: selectedCategoryId }]);
            setRemove({ url, method });
            setOpenModal('');
        }
        if (error) {
            showError(error ? error.toString() : "Failed to reassign transactions");
        }
    }, [reassign, selectedCategoryId]);

    const handleCreate = (categoryData: any) => {
        const { url, method } = buildApiRequestConfig(routes.categories.create);
        setCreate({ url, method, body: {
            ...categoryData, entityId: user?.entityId, searchString: categoryData.searchStrings
        }});
    };

    const handleUpdate = (categoryData: any) => {
        const { url, method } = buildApiRequestConfig(routes.categories.update, [{ param: 'id', value: selectedCategoryId }]);
        setUpdate({ url, method, body: { ...categoryData, searchString: categoryData.searchStrings } });
    };

    const handleDelete = () => {
        if (!selectedCategoryId) return;

        // Find the selected category to check transactionCount
        const findCategory = (cats: Category[], id: number): Category | null => {
            for (const cat of cats) {
                if (cat.id === id) return cat;
                if (cat.children) {
                    const found = findCategory(cat.children, id);
                    if (found) return found;
                }
            }
            return null;
        };

        const selectedCategory = findCategory(categories, selectedCategoryId);
        
        if (selectedCategory && selectedCategory.transactionCount && selectedCategory.transactionCount > 0) {
            // Show the unable to delete dialog
            setOpenModal('unable');
        } else {
            // Proceed with deletion
            const { url, method } = buildApiRequestConfig(routes.categories.delete, [{ param: 'id', value: selectedCategoryId }]);
            setRemove({ url, method });
        }
    };

    const handleReassign = (newCategoryId: number, applyDefaultTaxes: boolean) => {
        if (!selectedCategoryId) return;
        const { url, method } = buildApiRequestConfig(routes.categories.reassign);
        setReassign({ url, method, body: { oldCategoryId: selectedCategoryId, newCategoryId, entityId: user?.entityId, applyDefaultTaxes } });
    };

    const handleImportProgress = React.useCallback((state: any) => {
        // This callback is passed to ImportCategoryPopup and called with progress updates
        console.log('Import progress:', state);
    }, []);

    const handleConfirmImport = async (categoriesToImport: any[], importIdFromEvent: string, onProgress?: (state: any) => void) => {
        const CHUNK_SIZE = 100;
        
        // Get column names from importFileDetails (should be [codeColumnName, nameColumnName])
        const headers = importFileDetails?.headers || [];
        const codeColumnName = headers[0] || 'Code';
        const nameColumnName = headers[1] || 'Name';
        
        console.log('handleConfirmImport called with:', { 
            categoriesToImportLength: categoriesToImport.length,
            headers,
            codeColumnName,
            nameColumnName,
            firstRow: categoriesToImport[0]
        });
        
        // Map rows to proper category structure, using the actual column names
        const mappedCategories = categoriesToImport.map((row: any) => ({
            entityId: user?.entityId,
            code: (row[codeColumnName] || '').trim(),
            name: (row[nameColumnName] || '').trim(),
            parentCode: row.parentCode || null
        }));
        
        const chunks = [];
        for (let i = 0; i < mappedCategories.length; i += CHUNK_SIZE) {
            chunks.push(mappedCategories.slice(i, i + CHUNK_SIZE));
        }
        
        if (onProgress) {
            onProgress({
                currentChunk: 0,
                totalChunks: chunks.length,
                processedCategories: 0,
                totalCategories: mappedCategories.length,
                status: 'processing',
                importedEntries: []
            });
        }
        
        let successCount = 0;
        let errorCount = 0;
        const allImportedEntries: any[] = [];
        
        try {
            // Process chunks sequentially to avoid overwhelming the server
            for (let i = 0; i < chunks.length; i++) {
                const chunk = chunks[i];
                const { url, method } = buildApiRequestConfig(routes.categories.import);
                
                try {
                    // Make direct axios call instead of using useApi hook
                    const token = Cookies.get('token');
                    let headers: Record<string, string> = {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    };

                    if (token) {
                        headers['Authorization'] = `Bearer ${token}`;
                    }

                    const response = await axios({
                        method,
                        url,
                        headers,
                        data: {
                            categories: chunk,
                            importId: importIdFromEvent
                        }
                    });
                    
                    if (response.status >= 200 && response.status < 300) {
                        successCount += chunk.length;
                        
                        // Extract created entries from response and add to accumulated list
                        const createdEntries = response.data?.created || [];
                        allImportedEntries.push(...createdEntries);
                        
                        // Update progress
                        if (onProgress) {
                            onProgress({
                                currentChunk: i + 1,
                                totalChunks: chunks.length,
                                processedCategories: successCount,
                                totalCategories: mappedCategories.length,
                                status: 'processing',
                                importedEntries: allImportedEntries
                            });
                        }
                    } else {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }
                    
                    // Add a small delay between chunks to avoid overwhelming the server
                    if (i < chunks.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                    
                } catch (error: any) {
                    errorCount += chunk.length;
                    const currentSuccessCount = successCount;
                    if (onProgress) {
                        onProgress({
                            currentChunk: i + 1,
                            totalChunks: chunks.length,
                            processedCategories: currentSuccessCount,
                            totalCategories: mappedCategories.length,
                            status: 'error',
                            importedEntries: allImportedEntries
                        });
                    }
                }
            }
            
            // Final status update
            const finalStatus = errorCount === 0 ? 'success' : 'error';
            if (onProgress) {
                onProgress({
                    currentChunk: chunks.length,
                    totalChunks: chunks.length,
                    processedCategories: successCount,
                    totalCategories: mappedCategories.length,
                    status: finalStatus,
                    importedEntries: allImportedEntries
                });
            }
            
            // Show final result
            if (errorCount > 0) {
                showError(`Import completed with errors: ${successCount} successful, ${errorCount} failed`);
            } else {
                showSuccess(`Successfully imported ${successCount} categories`);
            }
            
            // Refresh categories list
            const params = [
                { param: 'entityId', value: user?.entityId },
                { param: 'startDate', value: dateRange.start?.toISOString().split('T')[0] },
                { param: 'endDate', value: dateRange.end.toISOString().split('T')[0] },
            ];
            const { url, method } = buildApiRequestConfig(routes.categories.fetchByEntity, params);
            setFetch({ url, method });
            
        } catch (error) {
            showError('Failed to import categories. Please try again.');
        }
    };


    React.useEffect(() => {
        const { data, isLoading, error } = createImport;
        if (!isLoading && data) {
            const newImportId = data.importEvent?.id || data.id;
            setImportId(newImportId);
        }
        if (error) {
            showError(typeof error === 'string' ? error : 'Failed to initialize import. Please try again.');
        }
    }, [createImport, showError]);

    const getCategoryTreeOptions = (): TreeDropdownOption[] => {
        const transform = (cats: Category[]): TreeDropdownOption[] => {
            return cats
                .filter(cat => cat.id !== selectedCategoryId) // Exclude the category being deleted
                .map(cat => ({
                    id: String(cat.id),
                    label: cat.name,
                    children: cat.children ? transform(cat.children) : undefined,
                }));
        };
        return transform(categories);
    };

    return (
        <div style={{ width: '100%' }}>
            <AddCategoryPopup
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() => {setOpenModal(''); setSelectedCategoryId(null);}}
                onCreate={openModal === 'edit' ? handleUpdate : handleCreate}
                isEdit={{ id: selectedCategoryId, state: openModal === 'edit' }}
                isChild={!!parent}
                parent={parent || null}
                takenData={categories.map(cat => ({ code: cat.code, name: cat.name, searchStrings: cat.searchStrings })) || []}
                entityId={user?.entityId}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Category?"
                description="This action will permanently remove the selected category."
                image={{ src: categoryImage, width: 150, height: 150 }}
                onCancel={() => {setOpenModal(''); setSelectedCategoryId(null);}}
                onConfirm={handleDelete}
            />
            <DeleteModal
                open={openModal === 'refuse'}
                message="Deletion Failed"
                description="Deletion refuse as child category exist"
                image={{ src: refuseImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    setOpenModal('unable');
                }}
            />
            <DeleteModal
                open={openModal === 'unable'}
                message="Unable to Delete Category?"
                description={`This category cannot be deleted because it has ${categories.find(c => {
                    const findInChildren = (cats: Category[], id: number): Category | null => {
                        for (const cat of cats) {
                            if (cat.id === id) return cat;
                            if (cat.children) {
                                const found = findInChildren(cat.children, id);
                                if (found) return found;
                            }
                        }
                        return null;
                    };
                    return c.id === selectedCategoryId || (c.children && !!findInChildren(c.children, selectedCategoryId || -1));
                })?.transactionCount || 0} transaction(s) currently assigned to this category. Deleting it would leave those transactions unassigned.`}
                footer="Do you want to reassign them to a new category?"
                red
                confirmLabel='Reassign Transactions'
                image={{ src: categoryImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    setOpenModal('reassign');
                }}
            />
            <ReassignCategoryModal
                open={openModal === 'reassign'}
                transactionCount={categories.find(c => {
                    const findInChildren = (cats: Category[], id: number): Category | null => {
                        for (const cat of cats) {
                            if (cat.id === id) return cat;
                            if (cat.children) {
                                const found = findInChildren(cat.children, id);
                                if (found) return found;
                            }
                        }
                        return null;
                    };
                    return c.id === selectedCategoryId || (c.children && !!findInChildren(c.children, selectedCategoryId || -1));
                })?.transactionCount || 0}
                categoryOptions={getCategoryTreeOptions()}
                oldCategoryId={selectedCategoryId}
                onCancel={() => setOpenModal('')}
                onConfirm={handleReassign}
                loading={reassign.isLoading}
            />
            <DeleteModal
                open={openNoProviderPopup}
                message="No Category Provider"
                description="There are no category providers configured. Please contact your administrator."
                onCancel={() => setOpenNoProviderPopup(false)}
                onConfirm={() => setOpenNoProviderPopup(false)}
            />
            <ImportCategoryPopup
                open={openModal === 'import'}
                onCancel={() => { 
                    setOpenModal(''); 
                    setImportFileDetails(null); 
                    setImportId(null);
                }}
                onEnterEditDataStep={React.useCallback(() => {
                    // Create import event when entering edit data step (step 3)
                    
                    const { url, method } = buildApiRequestConfig(routes.categories.createImportEvent);
                    setCreateImport({
                        method,
                        url,
                        body: {
                            entityId: user?.entityId,
                            moduleName: 'categories',
                            fileName: importFileDetails?.file?.name || 'imported_file.csv'
                        }
                    });
                }, [user?.entityId, importFileDetails?.file?.name])}
                onImportProgress={handleImportProgress}
                onImport={(selectedRows: any[], selectedRowIds: Set<string>, onProgress?: (state: any) => void) => {
                    if (selectedRows && selectedRows.length > 0 && importId) {
                        handleConfirmImport(selectedRows, importId, onProgress);
                        // Don't close modal yet - let import progress step handle closing
                        setImportFileDetails(null);
                        setImportId(null);
                    } 
                }}
                fileDetails={importFileDetails}
                onFileChange={setImportFileDetails}
                onProviderChange={() => { 
                    setImportFileDetails(null);
                }}
                loading={false}
            />
            <Topbar 
                title='Categories'
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                {!isMobile && (
                    <Box display="flex" alignItems="center" gap={2} width="100%" justifyContent="flex-end">
                        <XeroSyncButton type="gl" />
                        <DateRangeFilter start={dateRange.start || new Date()} end={dateRange.end} onChange={(start, end) => setDateRange({ start, end })} />
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!can('categories.import', 'create')}
                            onClick={handleImportClick}
                            sx={{
                                color: 'var(--color-primary)',
                                height: 34,
                                display: 'flex',
                                gap: 1,
                                border: '1px solid var(--color-primary)',
                                backgroundColor: 'var(--color-page-background)',
                                '&:hover': { backgroundColor: 'rgba(var(--color-primary-rgb), 0.0863)' }
                            }}
                        >
                            <Upload size={16} color="var(--color-primary)"/>
                            Import
                        </Button>
                        <AddBtn disabled={!canCreate} onClick={() => { setOpenModal('add'); setParent(null); }} label="Add Category" />
                    </Box>
                )}
            </Topbar>
            {isMobile && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        position: 'sticky',
                        top: 72,
                        zIndex: 2,
                        background: 'var(--color-page-background)',
                        borderBottom: '1px solid #f0f1f3',
                    }}
                >
                    <XeroSyncButton type="gl" compact />
                    <DateRangeFilter
                        start={dateRange.start || new Date()}
                        end={dateRange.end}
                        onChange={(start, end) => setDateRange({ start, end })}
                        sx={{ height: 34, minHeight: 34 }}
                    />
                </Box>
            )}
            <div style={{ padding: isMobile ? 0 : 10 }}>
                <Paper elevation={isMobile ? 0 : 1} sx={{ padding: isMobile ? 1 : 4, borderRadius: 0, boxShadow: isMobile ? 'none' : undefined, background: isMobile ? 'var(--color-container-background)' : 'var(--color-page-background)', boxSizing: 'border-box' }}>
                    <CategoryTable
                        canCreate={canCreate}
                        canUpdate={canUpdate}
                        canDelete={canDelete}
                        onEditCategory={(cat) => {setOpenModal('edit'); setParent(cat.parent ?? null); setSelectedCategoryId(cat.id);}}
                        onDeleteCategory={(id) => {
                            const findCategory = (cats: Category[], targetId: number): Category | null => {
                                for (const cat of cats) {
                                    if (cat.id === targetId) return cat;
                                    if (cat.children) { const found = findCategory(cat.children, targetId); if (found) return found; }
                                }
                                return null;
                            };
                            const cat = findCategory(categories, id);
                            if (cat?.children && cat.children.length > 0) {
                                showError('Deletion refused as child category exist');
                                return;
                            }
                            setSelectedCategoryId(id);
                            if (cat?.transactionCount && cat.transactionCount > 0) {
                                setOpenModal('unable');
                                return;
                            }
                            setOpenModal('delete');
                        }}
                        onAddSubCategory={(parent) => {setOpenModal('add'); setParent({ code: parent.code, id: parent.id });}}
                        onFilter={(field, value) => {
                            if (field === 'code') setFilter(prev => ({ ...prev, codes: Array.isArray(value) ? value : [] }));
                            else setFilter(prev => ({ ...prev, name: typeof value === 'string' ? value : '' }));
                        }}
                        onSort={(field, direction) => setSort({ field, direction })}
                        categories={categories}
                        availableCodes={allCodes}
                        stage={initialLoadComplete ? 'complete' : 'initialize'}
                    />
                </Paper>
            </div>
            {isMobile && (
                <>
                    <Fab
                        onClick={(e) => setFabMenuAnchor(e.currentTarget)}
                        sx={{
                            position: 'fixed',
                            bottom: 20,
                            right: 20,
                            background: 'var(--color-primary)',
                            color: '#fff',
                            '&:hover': { background: 'var(--color-primary-hover)' },
                            zIndex: 999,
                        }}
                    >
                        <Plus size={24} />
                    </Fab>
                    <MuiMenu
                        anchorEl={fabMenuAnchor}
                        open={Boolean(fabMenuAnchor)}
                        onClose={() => setFabMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <MenuItem
                            disabled={!canCreate}
                            onClick={() => { setOpenModal('add'); setParent(null); setFabMenuAnchor(null); }}
                            sx={{ display: 'flex', gap: 1 }}
                        >
                            <Plus size={16} color="var(--color-primary)" />
                            Add Category
                        </MenuItem>
                        <MenuItem
                            disabled={!can('categories.import', 'create')}
                            onClick={() => { handleImportClick(); setFabMenuAnchor(null); }}
                            sx={{ display: 'flex', gap: 1 }}
                        >
                            <Upload size={16} color="var(--color-primary)" />
                            Import
                        </MenuItem>
                    </MuiMenu>
                </>
            )}
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
        </div>
    );
};

export default CategoriesPage;
